import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  const handleCheckout = () => {
    const message = `Hello! I'm interested in purchasing:\n\n${cartItems
      .map(item => `${item.name} x${item.quantity} - ₹${item.price * item.quantity}`)
      .join('\n')}\n\nTotal: ₹${getCartTotal()}`;
    
    const whatsappUrl = `https://wa.me/919513556777?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <ShoppingBag className="w-24 h-24 mx-auto text-rose-200 mb-6" />
          <h1 className="text-3xl font-serif text-rose-900 mb-4">Your Cart is Empty</h1>
          <p className="text-rose-600 mb-8">Start adding some beautiful handcrafted jewelry!</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-rose-800 hover:bg-rose-900 text-white"
          >
            Browse Collection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-rose-800 hover:text-rose-900 hover:bg-rose-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Continue Shopping
        </Button>

        <h1 className="text-4xl font-serif text-rose-900 mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item._id} className="border-rose-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <div className="w-32 h-32 flex-shrink-0 bg-white rounded-lg overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium text-rose-900 mb-2">{item.name}</h3>
                      <p className="text-xl font-serif text-rose-800 mb-4">₹{item.price}</p>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 border border-rose-200 rounded-lg">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateQuantity(item._id, item.quantity - 1)}
                            className="text-rose-800 hover:text-rose-900 hover:bg-rose-50"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-rose-900 font-medium w-8 text-center">
                            {item.quantity}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateQuantity(item._id, item.quantity + 1)}
                            className="text-rose-800 hover:text-rose-900 hover:bg-rose-50"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(item._id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-serif text-rose-900">
                        ₹{item.price * item.quantity}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="lg:col-span-1">
            <Card className="border-rose-100 sticky top-4">
              <CardContent className="p-6">
                <h2 className="text-2xl font-serif text-rose-900 mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-rose-700">
                    <span>Subtotal</span>
                    <span>₹{getCartTotal()}</span>
                  </div>
                  <div className="flex justify-between text-rose-700">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="border-t border-rose-200 pt-3 mt-3">
                    <div className="flex justify-between text-xl font-serif text-rose-900">
                      <span>Total</span>
                      <span>₹{getCartTotal()}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  className="w-full mb-3 bg-rose-800 hover:bg-rose-900 text-white py-6 text-lg"
                >
                  Contact to Purchase
                </Button>
                
                <Button
                  onClick={clearCart}
                  variant="outline"
                  className="w-full text-rose-800 border-rose-300 hover:bg-rose-50"
                >
                  Clear Cart
                </Button>

                <div className="mt-6 p-4 bg-rose-50 rounded-lg">
                  <p className="text-sm text-rose-700">
                    Click "Contact to Purchase" to complete your order via WhatsApp
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
